<script setup>
import { vacancyFormValidationSchema } from '@/configs/validation';
import { routes } from '@/configs/menu';

defineProps({
  inSidebar: {
    type: Boolean,
    default: false,
  },
});

const vacanciesStore = useVacanciesStore();
const route = useRoute();

// captcha
const captchaRef = ref(null);
function resetCaptcha() {
  captchaRef.value.reset();
}

async function submit(values, actions) {
  try {
    await vacanciesStore.register({
      vacancyId: route.params.vacancySlug,
      name: values.name,
      city: values.city,
      phone: values.phone,
      email: values.email,
      message: values.message,
      attachment: values.attachment,
      captcha: values.captcha,
    });
  } catch (error) {
    resetCaptcha();

    const { errors } = error;

    if (errors) {
      actions.setErrors(errors);
    } else {
      alert(error);
    }

    throw error;
  }
}

const validationSchema = vacancyFormValidationSchema();

const submitRef = ref(null);
</script>

<template>
  <div class="ctr-vacancy-form" :class="{ container: !inSidebar }">
    <UiForm
      :rows-count="4"
      :validation-schema="validationSchema"
      :submit-callback="submit"
      class="ctr-vacancy-form__form"
      @submit-reset="resetCaptcha"
    >
      <template #row-1>
        <FormInput name="name" :label="$t('form.fields.fio')" />
      </template>

      <template #row-2>
        <FormInput name="city" :label="$t('form.fields.city')" />

        <FormInput
          name="phone"
          :label="$t('form.fields.phone')"
          mask="+{7} (000) 000-00-00"
          placeholder="+7 (000) 000-00-00"
        />

        <FormInput name="email" :label="$t('form.fields.email')" />
      </template>

      <template #row-3>
        <FormTextarea
          class="ctr-vacancy-form__field ctr-vacancy-form__field--about"
          name="message"
          :label="$t('form.fields.aboutYourselfOptional')"
        />
      </template>

      <template #row-4>
        <FormFile
          name="attachment"
          :label="$t('form.fields.resume')"
          :file-types="['doc', 'pdf', 'txt', 'xls']"
          :file-types-accept="['doc', 'pdf', 'txt', 'xls', 'docx', 'xlsx']"
        />
      </template>

      <template #footer="{ isFormSubmitting, isFormSubmitted, formState }">
        <div class="ctr-vacancy-form__footer">
          <div class="ctr-vacancy-form__row">
            <FormCheckbox name="agreement">
              {{ $t('form.fields.personalAgreement') }}
              <NuxtLink
                :to="routes.processingOfPersonalData.url"
                target="_blank"
                class="link"
              >
                {{ $t('form.fields.personalAgreementLink') }}
              </NuxtLink>
            </FormCheckbox>
          </div>

          <div class="ctr-vacancy-form__row">
            <FormYCaptcha
              ref="captchaRef"
              name="captcha"
              class="ctr-vacancy-form__captcha"
            />

            <ButtonsUnderlined
              ref="submitRef"
              class="ctr-vacancy-form__button"
              :title="$t(`form.submitVacancy.${formState}`)"
              :disabled="isFormSubmitting"
              :type="isFormSubmitted ? undefined : 'submit'"
              :icon="isFormSubmitted ? 'check-circle' : 'link-arrow-right'"
              :url="isFormSubmitted ? '/vacancies' : undefined"
              :target="isFormSubmitted ? '_self' : undefined"
            />

            <ClientOnly>
              <UiTooltip
                :reference-ref="submitRef?.$el"
                :show="isFormSubmitted"
                :title="$t('vacancies.form.thankYou.title')"
                :text="$t('vacancies.form.thankYou.text')"
                same-width
              />

              <template #fallback />
            </ClientOnly>
          </div>
        </div>
      </template>
    </UiForm>
  </div>
</template>

<style scoped lang="scss">
.ctr-vacancy-form {
  &__form {
    @include media-breakpoint-up(sm) {
      padding-right: em($gap-right-safe);
    }
  }

  &__field {
    &--about {
      height: em(292);

      @include media-breakpoint-down(sm) {
        height: em(205);
      }
    }
  }

  &__footer {
    margin-top: em(31);
    display: flex;
    flex-direction: column;
    gap: em(32);

    @include media-breakpoint-down(sm) {
      margin-top: em(24);
      gap: em(22);
    }
  }

  &__row {
    display: flex;
    gap: em(92);

    @include media-breakpoint-up(sm) {
      align-items: flex-start;
    }

    @include media-breakpoint-down(sm) {
      flex-direction: column;
      gap: em(50);
    }
  }

  &__captcha {
    flex-grow: 1;
  }

  &__button {
    width: em(401);
    flex-shrink: 0;

    @include media-breakpoint-down(sm) {
      width: 100%;
    }
  }
}
</style>
